import { MoneyOffRounded as MoneyOffRoundedIcon } from 'components/icons';
import Chip from 'components/@extended/Chip';
import MatterIndicators, { BOTTOM_LINE_IN_NEW_DESIGN, hasMatterAccess } from 'components/matters/MatterIndicators';
import { FormattedDate } from 'components/misc/FormattedDate';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import LineClamp from 'components/misc/LineClamp';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { DataTableColumnDef } from 'components/tables/data-table';
import { Checkbox } from 'components/ui/checkbox';
import CopyToClipboardIcon, { REVEAL_ON_COPY_GROUP_HOVER_CLASS } from 'components/util/CopyToClipboard';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { CellContext } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { ClassifierData } from 'types/classifiers';
import { NamedEntityMinDto } from 'types/other';
import { TimecardListItemData, TimecardsTotalsDto } from 'types/timecards';
import { cn } from 'utils/cn';
import { secondsToHours } from 'utils/time';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { getClassifierText, t } from 'utils/getTranslations';
import { TimecardStatusLabel } from '../labels/TimecardStatusLabel';
import { isTimecardSelectable } from '../utils/timecardUtils';
import { ClosedCaseDraftMarker } from './ClosedCaseDraftMarker';
import { MoreCountBadge, TooltipLines } from './MoreCountBadge';
import { TimecardRowActions } from './TimecardRowActions';

export type TimecardColumn = DataTableColumnDef<TimecardListItemData, TimecardsTotalsDto | undefined>;

type Options = {
    classifiers: Record<string, ClassifierData>;
    /** Employee-name formatter, so the author column follows the tenant's name order. */
    formatName: (employee: NamedEntityMinDto) => string;
    /**
     * Bulk selection, offered only where bulk actions are (a case tab). Its checkbox column is the
     * sticky prefix's first cell, ahead of the row markers.
     */
    selection?: {
        selectedIds: number[];
        onToggle: (timecardId: number, isSelected: boolean) => void;
    };
    /**
     * True on a case tab, where every row shares one case. The case indicators then belong to the
     * case page header and are not repeated per row — once per case per view.
     */
    isCaseScoped?: boolean;
};

/** Empty cell marker, so a blank column reads as "nothing here" rather than as a rendering fault. */
const EMPTY = '–';

/**
 * Totals-row cell for the two hours columns. A component rather than a plain node because the
 * tenant's display precision comes from a hook, and it is what turns the summed seconds the API
 * publishes into the hours the column shows.
 */
const HoursTotal = ({ seconds }: { seconds: number | undefined }) => {
    const { displayDecimalScale } = useTimecardPrecision();
    return <HoursDisplay value={secondsToHours(seconds ?? 0, displayDecimalScale)} />;
};

/** Totals-row cell for the two hours columns, given the field of the totals DTO that holds them. */
const renderHoursTotal =
    (field: 'totalUsedTimeInSeconds' | 'totalBilledTimeInSeconds') =>
    (totals: TimecardsTotalsDto | undefined): ReactNode =>
        totals ? <HoursTotal seconds={totals[field]} /> : null;

/**
 * Totals-row cell for the Fees column. The API sums in the tenant base currency only — the
 * per-row figure the column leads with is each timecard's own currency, which no total can add up.
 */
const renderFeesTotal = (totals: TimecardsTotalsDto | undefined): ReactNode =>
    totals ? (
        <MoneyDisplay amount={totals.totalFeesInBaseCurrency ?? 0} currency={totals.baseCurrencyTypeCode} />
    ) : null;

const ACTION_SLOT_PX = 32;
const ACTION_SLOT_GAP_PX = 4;
const CELL_INSET_PX = 32;

/**
 * Width of an actions column holding `slots` reserved controls. Each slot is counted with its own
 * gap rather than only the gaps between them: measured in the browser, the row needs that extra
 * one, and a managed cell clips rather than wrapping.
 */
function actionsWidth(slots: number): number {
    return slots * (ACTION_SLOT_PX + ACTION_SLOT_GAP_PX) + CELL_INSET_PX;
}

/**
 * The red blocked icon, which stands in for a blocked case's actions: drawn in the same round 32px
 * footprint as an action, with no label.
 */
const ACTION_TAG_PX = ACTION_SLOT_PX;

/**
 * The actions column is as wide as the widest thing it can hold: an own draft's seven slots, or a
 * blocked case's view + edit slot + icon + kebab — whichever is wider, so every surface uses
 * the one width — a managed cell clips rather than wrapping, and no amount of horizontal scrolling
 * reveals what a cell cut off.
 */
const ACTIONS_WIDTH = Math.max(actionsWidth(7), actionsWidth(3) + ACTION_TAG_PX);

/**
 * One-line cell text that ellipsizes rather than wrapping.
 *
 * The important modifier is load-bearing on a LINK: the cell's own `.entity-link` rule sets
 * `white-space: normal` from an unlayered stylesheet, which
 * beats a Tailwind utility. Without it a long value wraps, and a fixed-height row then cuts the
 * second line off instead of showing an ellipsis. Plain text needs no such help, which is why the
 * practice-area cells ellipsize on `truncate` alone.
 */
const ONE_LINE_LINK = 'truncate whitespace-nowrap!';

/** A link in a cell: the cell's `.entity-link` rule colours it, and it underlines on hover. */
const CELL_LINK = 'entity-link w-fit no-underline hover:underline';

/**
 * The invoices the cell had no room for, one per line as `number · status`.
 *
 * Only those: the badge counts what is missing, so listing the linked one again would make `+1`
 * open two lines. Each line is a link, since the tooltip is the only way to reach them — reachable
 * because the tooltip is interactive, so the pointer can travel into it without it closing.
 *
 * In classic these carry the app's ordinary hyperlink treatment — black, blue on hover — unlike the
 * number in the cell itself, which is blue at rest. The tooltip is portaled out of the table, so
 * the cell's link rule never reaches these and their own utilities decide the colour. In the new
 * design each line is the invoice's chip.
 */
function InvoiceTooltipLines({
    invoices,
    classifiers,
}: {
    invoices: NonNullable<TimecardListItemData['invoices']>;
    classifiers: Record<string, ClassifierData>;
}): JSX.Element {
    return (
        <span className="flex flex-col">
            {invoices.map((invoice) => (
                <RecordLink
                    key={invoice.id}
                    to={`/invoice/${invoice.id}`}
                    target="_blank"
                    className={cn(CELL_LINK, 'text-fg hover:text-link whitespace-nowrap')}
                    label={[
                        invoice.invoiceNumber,
                        getClassifierText(classifiers['INVOICE_STATUS_TYPE'], invoice.invoiceStatusTypeCode),
                    ]
                        .filter(Boolean)
                        .join(' \u00B7 ')}
                    withOrigin
                />
            ))}
        </span>
    );
}

/**
 * Keeps the `+N` badge whole and lets the value beside it give way instead.
 *
 * A managed cell is `overflow: hidden` with no ellipsis, so a nowrap value simply pushes whatever
 * follows it past the cell edge — and the badge is the half a reader cannot infer, since it is the
 * only sign the row has more than the one item on show. The first child therefore shrinks and
 * ellipsizes while the badge does not.
 *
 * `*+*` and not `:last-child`: with nothing to count, the value is the last child as well as the
 * first, and a `shrink-0` on it would deny the very ellipsis this exists to give it.
 */
const CLAMPED_ROW_CLASS = 'min-w-0 [&>*:first-child]:min-w-0 [&>*:first-child]:truncate [&>*+*]:shrink-0';

function practiceAreaColumn(id: string, header: string, getList: (row: TimecardListItemData) => NamedEntityMinDto[]) {
    return {
        id,
        header,
        enableSorting: false,
        // Floored wide enough for a practice-area name plus the badge: these columns are opt-in, so a
        // reader who turns one on wants to read it, and the engine answers a floor it cannot honour
        // with horizontal scroll rather than by clipping.
        meta: { widthWeight: 1.6, minPx: 220, defaultHidden: true },
        cell: ({ row }: CellContext<TimecardListItemData, unknown>) => {
            const practiceAreas = getList(row.original);
            const [first, ...rest] = practiceAreas;
            if (!first) {
                return EMPTY;
            }
            return (
                <span className={cn('flex items-center gap-1', CLAMPED_ROW_CLASS)}>
                    <EllipsisTooltip>{first.name}</EllipsisTooltip>
                    {rest.length > 0 ? (
                        <MoreCountBadge
                            count={rest.length}
                            title={<TooltipLines lines={rest.map((area) => area.name)} />}
                        />
                    ) : null}
                </span>
            );
        },
    } satisfies TimecardColumn;
}

/**
 * The timecards list's columns, in their default order.
 *
 * Widths are weights against the container, not pixels: every column keeps its share as the viewport
 * changes, with `minPx` as the floor its content needs. What a resize frees goes to the details of
 * work done alone, flagged `absorbSlack` — narrowing a column widens that one instead of every
 * column growing a weighted share of the space. The two pinned columns are the exception —
 * `fixedPx` freezes them and, as a side effect, keeps them out of the column picker, which is what
 * makes the row markers and the actions unhideable.
 *
 * Sort keys are the backend's own property paths; `sortAccessor` carries them wherever they differ
 * from the column id. Fees sort on the base-currency amount, which is the only figure comparable
 * across a mixed-currency list. A column is sortable only when it also declares an ACCESSOR — the
 * table engine gates the sort control on one — even for a cell that renders from `row.original` and
 * never reads that value.
 *
 * `nullHandling: 'nullsLast'` goes on the sortable columns that render nothing for a missing value,
 * so a row with an empty cell sinks to the bottom in BOTH directions rather than heading the list on
 * a descending sort (Postgres orders nulls first there by default). Those are the activity type, the
 * author's office and the case assignment: each shows the empty marker, and each is null-reachable
 * in SQL — a nullable column for the first, a left join for the other two.
 *
 * NOT the fee, whose key is null-reachable too (a correlated subquery, empty when the rate chain
 * finds no row) but whose cell renders that as a formatted zero. Sorting it `nullsLast` would put a
 * row reading `0.00` below the largest fee in the list while a genuine zero sat at the top, so the
 * two ends of one ascending sort would show the same figure. The zero is the display this list wants
 * (the group ribbon shows one too), which leaves the sort as the half that has to give.
 *
 * The remaining keys cannot be empty at all: the date, hours, status, author and case are NOT NULL or
 * built with `concat` (which folds a null into an empty string), and the client's name comes through
 * an inner join to a NOT NULL column.
 */
export function getTimecardListColumns({
    classifiers,
    formatName,
    selection,
    isCaseScoped,
}: Options): TimecardColumn[] {
    return [
        ...(selection
            ? [
                  {
                      id: 'select',
                      enableSorting: false,
                      meta: { fixedPx: 64, sticky: true },
                      cell: ({ row }: CellContext<TimecardListItemData, unknown>) =>
                          isTimecardSelectable(row.original) ? (
                              <Checkbox
                                  variant="secondary"
                                  checked={selection.selectedIds.includes(row.original.id)}
                                  onCheckedChange={(checked) => selection.onToggle(row.original.id, checked)}
                                  aria-label={t('invoices.tooltips.bulk-selection')}
                              />
                          ) : null,
                  } satisfies TimecardColumn,
              ]
            : []),
        {
            id: 'nonBillable',
            header: '',
            enableSorting: false,
            // Room for the closed-case draft marker and the non-billable chip side by side, plus the
            // shared cell's own 32px inset. No padding override: the markers keep the same left edge
            // as every other column's content.
            meta: { fixedPx: 88, sticky: true, pickerLabel: t('timecards.form.is-non-billable') },
            cell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <ClosedCaseDraftMarker timecard={row.original} />
                    {row.original.isNonBillable ? (
                        <Chip
                            size="small"
                            label={<MoneyOffRoundedIcon titleAccess={t('timecards.form.is-non-billable')} />}
                            type="partyType"
                        />
                    ) : null}
                </div>
            ),
        },
        {
            id: 'timecardDate',
            header: t('date'),
            accessorKey: 'timecardDate',
            // The date plus the sort caret; measured at 112, so 110 clipped the year.
            meta: { widthWeight: 1, minPx: 130, pickerLabel: t('timecards.filter.timecard-date') },
            cell: ({ getValue }) => <FormattedDate value={getValue()} nowrap />,
        },
        {
            id: 'employee',
            header: t('timecards.filter.timecard-author'),
            accessorKey: 'employee',
            enableSorting: true,
            meta: { sortAccessor: 'employee.fullName', widthWeight: 1.3, minPx: 150 },
            cell: ({ row }) => (
                <EllipsisTooltip>
                    <RecordLink
                        to={`/employee/${row.original.employee.id}`}
                        target="_blank"
                        className={cn(CELL_LINK, ONE_LINE_LINK)}
                        label={formatName(row.original.employee)}
                        withOrigin
                        hideTitle
                    />
                </EllipsisTooltip>
            ),
        },
        {
            id: 'usedTime',
            header: t('timecards.list.columns.used-time'),
            accessorKey: 'totalTimeInSeconds',
            enableSorting: true,
            meta: {
                sortAccessor: 'totalTimeInSeconds',
                widthWeight: 1,
                minPx: 110,
                textAlign: 'right',
                renderTotal: renderHoursTotal('totalUsedTimeInSeconds'),
            },
            // Bold, as the pair of figures the list is read for.
            cell: ({ row }) => (
                <span className="font-bold">
                    <HoursDisplay value={row.original.timecardStats?.totalTimeInHours} />
                </span>
            ),
        },
        {
            id: 'billedTime',
            header: t('timecards.list.columns.billed-time'),
            accessorKey: 'billedTimeInSeconds',
            enableSorting: true,
            meta: {
                sortAccessor: 'billedTimeInSeconds',
                widthWeight: 1,
                minPx: 110,
                textAlign: 'right',
                renderTotal: renderHoursTotal('totalBilledTimeInSeconds'),
            },
            cell: ({ row }) => (
                <span className="font-bold">
                    <HoursDisplay value={row.original.timecardStats?.billedTimeInHours} />
                </span>
            ),
        },
        {
            id: 'client',
            header: t('timecards.list.columns.client'),
            accessorKey: 'matter',
            enableSorting: true,
            // A default column on every surface, the case and contact tabs included: the client is
            // fixed there, but a reader still wants to see whose case they are looking at.
            meta: { sortAccessor: 'matter.clientParty.name', widthWeight: 1.7, minPx: 160 },
            // `singleLine`, not a `LineClamp`: the name sits inside PartyName's flex chain, which
            // produces no line boxes for a clamp to count — it would wrap to three or four lines and
            // make the row taller than its neighbours.
            cell: ({ row }) =>
                row.original.matter.clientParty ? (
                    <EllipsisTooltip>
                        <PartyName
                            party={row.original.matter.clientParty}
                            isLink
                            useLinkWithOrigin
                            linkTarget="_blank"
                            variant="readonly-other"
                            showPartyNumber
                            singleLine
                            hideTitle
                        />
                    </EllipsisTooltip>
                ) : (
                    EMPTY
                ),
        },
        {
            id: 'matter',
            header: t('timecards.list.columns.case'),
            accessorKey: 'matter',
            enableSorting: true,
            meta: { sortAccessor: 'matter.fullName', widthWeight: 2, minPx: 200 },
            cell: ({ row }) => (
                <div className="new:flex-wrap flex min-w-0 items-start gap-1">
                    {!isCaseScoped && (
                        <MatterIndicators
                            isConfidential={row.original.matter.isConfidential}
                            hasAccess={hasMatterAccess(row.original.matter)}
                            className={BOTTOM_LINE_IN_NEW_DESIGN}
                        />
                    )}
                    <RecordLink
                        to={`/matter/${row.original.matter.id}`}
                        target="_blank"
                        className={cn(CELL_LINK, 'block min-w-0')}
                        label={`${row.original.matter.caseNumber} ${row.original.matter.name}`}
                        withOrigin
                    >
                        <EllipsisTooltip>
                            <span className="font-bold">{row.original.matter.caseNumber}</span>
                        </EllipsisTooltip>
                        <EllipsisTooltip>
                            <span className="text-caption text-grey-600">{row.original.matter.name}</span>
                        </EllipsisTooltip>
                    </RecordLink>
                </div>
            ),
        },
        {
            id: 'status',
            header: t('status.status'),
            accessorKey: 'timecardStatusTypeCode',
            meta: { sortAccessor: 'timecardStatusTypeCode', widthWeight: 1, minPx: 118 },
            cell: ({ row }) => (
                <TimecardStatusLabel
                    classifier={classifiers['TIMECARD_STATUS_TYPE']}
                    statusCode={row.original.timecardStatusTypeCode}
                />
            ),
        },
        {
            id: 'description',
            header: t('timecards.list.columns.description'),
            accessorKey: 'description',
            enableSorting: false,
            // The column that absorbs what a resize frees: narrowing any other column widens THIS
            // one, rather than every column taking a weighted share of the space — a date column has
            // nothing to do with more room, and this has the most to say with it.
            meta: { widthWeight: 2.5, minPx: 200, absorbSlack: true },
            cell: ({ row }) => {
                const { description } = row.original;
                return (
                    <div className="group/copy flex min-w-0 items-start">
                        <LineClamp maxLineCount={2} className="min-w-0">
                            {description}
                        </LineClamp>
                        {description ? (
                            <CopyToClipboardIcon
                                text={description}
                                className={cn('-my-0.5 shrink-0', REVEAL_ON_COPY_GROUP_HOVER_CLASS)}
                            />
                        ) : null}
                    </div>
                );
            },
        },
        {
            id: 'fees',
            header: t('timecards.list.columns.fees-total'),
            accessorKey: 'currencyDetails',
            enableSorting: true,
            meta: {
                sortAccessor: 'currencyDetails.totalAmountInBaseCurrency',
                widthWeight: 1.3,
                minPx: 140,
                textAlign: 'right',
                renderTotal: renderFeesTotal,
            },
            // Both lines always: the timecard's own currency, and underneath the base-currency value
            // it was converted to — the same pairing the card list showed.
            cell: ({ row }) => (
                <span className="flex min-w-0 flex-col items-end">
                    <MoneyDisplay
                        amount={row.original.timecardStats?.amountInOriginalCurrency ?? 0}
                        currency={row.original.timecardStats?.originalCurrencyTypeCode}
                    />
                    <span className="text-caption text-fg-secondary">
                        (
                        <MoneyDisplay
                            amount={row.original.currencyDetails?.totalAmountInBaseCurrency ?? 0}
                            currency={row.original.currencyDetails?.baseCurrency}
                        />
                        )
                    </span>
                </span>
            ),
        },
        {
            id: 'invoice',
            header: t('timecards.list.columns.invoice'),
            enableSorting: false,
            // An invoice number plus the badge counting the other invoices the timecard is billed on.
            meta: { widthWeight: 1.4, minPx: 180 },
            // The number links the newest invoice and the badge counts the rest, whose numbers and
            // statuses the tooltip lists — classic's blue treatment for the link, and its own
            // badge, which is why this does not go through the shared `ListClamp`.
            cell: ({ row }) => {
                const invoices = row.original.invoices ?? [];
                const [first, ...rest] = invoices;
                if (!first) {
                    return EMPTY;
                }
                return (
                    <span className={cn('flex items-center gap-1', CLAMPED_ROW_CLASS)}>
                        {/* Classic's link is blue at rest, unlike the black-hyperlink treatment the shared
                            cell rule gives a link — hence the important modifier, since that rule is
                            unlayered and would otherwise beat a utility class. */}
                        <EllipsisTooltip>
                            <RecordLink
                                to={`/invoice/${first.id}`}
                                target="_blank"
                                className={cn(CELL_LINK, ONE_LINE_LINK, 'text-link!')}
                                label={first.invoiceNumber}
                                withOrigin
                                hideTitle
                            />
                        </EllipsisTooltip>
                        {rest.length > 0 ? (
                            <MoreCountBadge
                                count={rest.length}
                                title={<InvoiceTooltipLines invoices={rest} classifiers={classifiers} />}
                            />
                        ) : null}
                    </span>
                );
            },
        },
        {
            id: 'activityType',
            header: t('timecards.list.columns.activity-type'),
            accessorKey: 'timecardActivityTypeCode',
            enableSorting: true,
            meta: {
                sortAccessor: 'timecardActivityTypeCode',
                nullHandling: 'nullsLast',
                widthWeight: 1.3,
                minPx: 140,
                defaultHidden: true,
            },
            cell: ({ getValue }) => {
                const label = getClassifierText(classifiers['TIMECARD_ACTIVITY_TYPE'], getValue());
                return label ? <EllipsisTooltip>{label}</EllipsisTooltip> : EMPTY;
            },
        },
        practiceAreaColumn(
            'practiceAreas',
            t('timecards.list.columns.timecard-practice-areas'),
            (timecard) => timecard.practiceAreas ?? []
        ),
        {
            id: 'office',
            header: t('timecards.list.columns.author-office'),
            accessorKey: 'employee',
            enableSorting: true,
            meta: {
                sortAccessor: 'employee.currentEmployment.office.name',
                nullHandling: 'nullsLast',
                widthWeight: 1.2,
                minPx: 130,
                defaultHidden: true,
            },
            cell: ({ row }) => {
                const employment = row.original.employee?.latestEmployment;
                if (!employment?.officeName) {
                    return EMPTY;
                }
                return (
                    <EllipsisTooltip>
                        {employment.officeId ? (
                            <RecordLink
                                to={`/office/${employment.officeId}`}
                                target="_blank"
                                className={cn(CELL_LINK, ONE_LINE_LINK)}
                                label={employment.officeName}
                                withOrigin
                                hideTitle
                            />
                        ) : (
                            employment.officeName
                        )}
                    </EllipsisTooltip>
                );
            },
        },
        practiceAreaColumn(
            'employeePracticeAreas',
            t('timecards.list.columns.author-practice-areas'),
            (timecard) => timecard.employeePracticeAreas ?? []
        ),
        practiceAreaColumn(
            'matterPracticeAreas',
            t('timecards.list.columns.case-practice-areas'),
            (timecard) => timecard.matterPracticeAreas ?? []
        ),
        {
            id: 'expenses',
            header: t('timecards.list.columns.expenses-total'),
            enableSorting: false,
            meta: { widthWeight: 1.2, minPx: 130, textAlign: 'right', defaultHidden: true },
            // One sum per currency, as the backend groups them; a timecard with no expense shows the
            // empty marker rather than a zero it never earned.
            cell: ({ row }) => {
                const expenseList = row.original.timecardStats?.expenseList ?? [];
                const sums = expenseList.flatMap((expensesByCurrency) =>
                    Object.entries(expensesByCurrency).map(([currency, amount]) => ({ currency, amount }))
                );
                if (sums.length === 0) {
                    return EMPTY;
                }
                return (
                    <span className="flex min-w-0 flex-col items-end">
                        {sums.map(({ currency, amount }) => (
                            <MoneyDisplay key={currency} amount={amount} currency={currency} />
                        ))}
                    </span>
                );
            },
        },
        {
            id: 'matterAssignment',
            header: t('timecards.list.columns.case-assignment'),
            accessorKey: 'matterAssignment',
            enableSorting: true,
            meta: {
                sortAccessor: 'matterAssignment.name',
                nullHandling: 'nullsLast',
                widthWeight: 1.5,
                minPx: 160,
                defaultHidden: true,
            },
            cell: ({ row }) => {
                const name = row.original.matterAssignment?.name;
                return name ? <EllipsisTooltip>{name}</EllipsisTooltip> : EMPTY;
            },
        },
        {
            id: 'actions',
            header: t('actions'),
            enableSorting: false,
            // Every action sits in a reserved 32px slot laid out from the cell's left edge, so the
            // width is the slot count: view, edit, expense, duplicate and the kebab, plus finish and
            // start-tracking on a draft surface. Slots, their 4px gaps, and the 32px cell inset.
            meta: { fixedPx: ACTIONS_WIDTH, pickerLabel: t('actions') },
            cell: ({ row }) => <TimecardRowActions timecard={row.original} />,
        },
    ];
}
